// src/features/task/detail/PropertyList.tsx
// The task's properties, as one panel.
//
// They used to be ten separate cards — priority, assignee, due date, progress,
// dates, type, tags, watchers and so on — each with its own border and its own
// heading, several of them showing nothing but a dash. That is the same
// information the create page fits in a single panel of quiet rows, so this is
// that panel, on the other side of the task's life.

import type { ReactNode } from 'react';
import { cn } from '@core/ui';

export function PropertyList({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('rounded-lg border border-border bg-card', className)}>
      <p className="px-3 pt-3 pb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <dl className="divide-y divide-border/60">{children}</dl>
    </div>
  );
}

interface PropertyItemProps {
  label: string;
  /** The value. A dash is supplied when this is empty, so rows stay legible. */
  children?: ReactNode;
  /** A control that belongs to this property — assign, pick a date, watch. */
  action?: ReactNode;
  /** Stack the value under the label; for values too wide to sit beside it. */
  stacked?: boolean;
}

export function PropertyItem({ label, children, action, stacked }: PropertyItemProps) {
  const empty = children === null || children === undefined || children === false;
  return (
    <div
      className={cn(
        'px-3 py-2',
        stacked ? 'space-y-1.5' : 'flex items-center justify-between gap-3',
      )}
    >
      <dt className="shrink-0 text-xs text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          'flex min-w-0 items-center gap-2 text-sm',
          stacked ? '' : 'justify-end text-right',
          empty ? 'text-muted-foreground' : 'text-foreground',
        )}
      >
        {empty ? <span aria-hidden="true">—</span> : children}
        {action}
      </dd>
    </div>
  );
}
